// The portal talks to /admin/v1 on its own origin (Caddy in production, the Vite proxy in
// development), so the session cookie travels without CORS and stays SameSite=Strict.
export const API_BASE = "/admin/v1";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields?: Record<string, string>,
    public requestId?: string,
  ) {
    super(message);
  }
}

export function csrfToken(): string {
  const m = document.cookie.match(/(?:^|;\s*)pf_csrf=([^;]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : "";
}

type StepUpHandler = () => Promise<boolean>;
type UnauthorizedHandler = (code: string) => void;
let stepUpHandler: StepUpHandler | null = null;
let unauthorizedHandler: UnauthorizedHandler | null = null;

// Registered by StepUpProvider: shows the password + code dialog and resolves true when confirmed.
export function onStepUpRequired(h: StepUpHandler | null) {
  stepUpHandler = h;
}

export function onUnauthorized(h: UnauthorizedHandler | null) {
  unauthorizedHandler = h;
}

type Options = { body?: unknown; raw?: Blob; query?: Record<string, string | number | boolean | undefined | null>; retry?: boolean };

export async function api<T>(method: string, path: string, opts: Options = {}): Promise<T> {
  let url = API_BASE + path;
  if (opts.query) {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.query)) if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
    const s = q.toString();
    if (s) url += (url.includes("?") ? "&" : "?") + s;
  }
  const headers: Record<string, string> = { Accept: "application/json" };
  if (method !== "GET") headers["X-CSRF-Token"] = csrfToken();
  let body: BodyInit | undefined;
  if (opts.raw) {
    body = opts.raw;
    headers["Content-Type"] = opts.raw.type || "application/octet-stream";
  } else if (opts.body !== undefined) {
    body = JSON.stringify(opts.body);
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(url, { method, headers, body, credentials: "same-origin" });
  if (res.status === 204) return undefined as T;
  const data = res.headers.get("content-type")?.includes("json") ? await res.json() : null;
  if (res.ok) return data as T;

  const err = data?.error ?? {};
  const apiError = new ApiError(res.status, err.code ?? "http_" + res.status, err.message ?? res.statusText, err.fields, err.request_id);
  if (res.status === 428 && err.code === "step_up_required" && stepUpHandler && opts.retry !== false) {
    if (await stepUpHandler()) return api<T>(method, path, { ...opts, retry: false });
  }
  if (res.status === 401 && unauthorizedHandler && !path.startsWith("/auth/login") && !path.startsWith("/auth/totp")) {
    unauthorizedHandler(apiError.code);
  }
  throw apiError;
}

export const get = <T>(path: string, query?: Options["query"]) => api<T>("GET", path, { query });
export const post = <T>(path: string, body?: unknown) => api<T>("POST", path, { body: body ?? {} });
export const put = <T>(path: string, body?: unknown) => api<T>("PUT", path, { body });
export const patch = <T>(path: string, body?: unknown) => api<T>("PATCH", path, { body });
export const del = <T>(path: string) => api<T>("DELETE", path);

export function fieldErrors(e: unknown): Record<string, string> {
  return e instanceof ApiError && e.fields ? e.fields : {};
}

export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof TypeError) return "Can't reach the server. Check your connection.";
  return "Something went wrong.";
}

export function newId(): string {
  return crypto.randomUUID();
}
